/**
 * Created: 2026-03-21
 * Initial commit: Add dashboard page with daily income chart
 *
 * Dashboard page showing daily income chart for the selected date range.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Alert, Box, Typography } from '@mui/material'
import type { BarItem } from '@mui/x-charts/BarChart'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { addDays, endOfDay, format, startOfDay } from 'date-fns'
import { useState } from 'react'
import DashboardRangePicker, { defaultRange } from '../components/DashboardRangePicker'
import {
  DailyIncomeDocument,
  DailyOccupancyDocument,
  TopCustomersDocument,
} from '../gql/graphql'
import { useRole } from '../hooks'

gql`
  query DailyIncome($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    dailyIncome(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      date
      amount
    }
  }

  query TopCustomers($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    topCustomers(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      name
      amount
      percentage
      count
    }
  }

  query DailyOccupancy($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    dailyOccupancy(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      date
      rates {
        sport
        rate
        capacity
        occupied
      }
    }
  }
`

export default function DashboardPage() {
  const { currentSite } = useRole()
  const [range, setRange] = useState<DateRange<Date>>(defaultRange)

  const [startDate, endDate] = range
  const queryStart = startDate ? startOfDay(startDate).toISOString() : null
  const queryEnd = endDate ? endOfDay(endDate).toISOString() : null

  const queryVars = {
    siteId: currentSite?.id ?? '',
    startDate: queryStart ?? '',
    endDate: queryEnd ?? '',
  }
  const skipQuery = !currentSite || !queryStart || !queryEnd

  const { data, loading, error } = useQuery(DailyIncomeDocument, {
    variables: queryVars,
    skip: skipQuery,
    fetchPolicy: 'network-only',
  })

  const {
    data: topData,
    loading: topLoading,
    error: topError,
  } = useQuery(TopCustomersDocument, {
    variables: queryVars,
    skip: skipQuery,
    fetchPolicy: 'network-only',
  })

  const { data: occupancyData, loading: occupancyLoading } = useQuery(
    DailyOccupancyDocument,
    {
      variables: queryVars,
      skip: skipQuery,
      fetchPolicy: 'network-only',
    },
  )

  // Build date list for x-axis
  const allDates: string[] = []
  if (
    startDate &&
    endDate &&
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime())
  ) {
    let current = startOfDay(startDate)
    const end = startOfDay(endDate)
    while (current <= end) {
      allDates.push(format(current, 'yyyy-MM-dd'))
      current = addDays(current, 1)
    }
  }

  // Income chart data
  const points = data?.dailyIncome ?? []
  const incomeMap = new Map(points.map((p) => [p.date, p.amount]))
  const xLabels = allDates
  const amounts = allDates.map((d) => incomeMap.get(d) ?? 0)
  const totalIncome = amounts.reduce((sum, v) => sum + v, 0)

  // Top customers chart data
  const raw = topData?.topCustomers ?? []
  const restRow = raw.find((c) => c.name === '其他')
  const topRows = raw.filter((c) => c.name !== '其他')
  const customerNames = topRows.map((c) => c.name)
  const customerAmounts = topRows.map((c) => c.amount)

  // Occupancy chart data
  const occupancyPoints = occupancyData?.dailyOccupancy ?? []
  const sportSet = new Set<string>()
  for (const p of occupancyPoints) {
    for (const r of p.rates) sportSet.add(r.sport)
  }
  const sports = Array.from(sportSet).sort()
  const sportLabels: Record<string, string> = { TENNIS: '网球', PICKLEBALL: '匹克球' }
  const occupancyMap = new Map(occupancyPoints.map((p) => [p.date, p.rates]))

  const occupancySeries = sports.map((sport) => ({
    label: sportLabels[sport] ?? sport,
    data: allDates.map((d) => {
      const rates = occupancyMap.get(d) ?? []
      return rates.find((r) => r.sport === sport)?.rate ?? 0
    }),
    showMark: false,
  }))

  const sportAverages = sports.map((sport) => {
    const values = allDates.map((d) => {
      const rates = occupancyMap.get(d) ?? []
      return rates.find((r) => r.sport === sport)?.rate ?? 0
    })
    const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0
    return { sport, label: sportLabels[sport] ?? sport, avg: Math.round(avg) }
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">数据看板</Typography>
        <Box sx={{ flex: 1 }} />
        <DashboardRangePicker range={range} onRangeChange={setRange} />
      </Box>

      {(error || topError) && (
        <Alert severity="error">加载数据失败: {(error ?? topError)?.message}</Alert>
      )}

      {/* Income chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">收入</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            合计：¥{totalIncome.toFixed(2)}
          </Typography>
        </Box>
        <BarChart
          loading={loading}
          xAxis={[
            { scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 11 } },
          ]}
          series={[{ data: amounts, label: '收入 (元)', color: '#1976d2' }]}
          height={360}
          margin={{ left: 70, right: 20, top: 20, bottom: 60 }}
          yAxis={[{ valueFormatter: (v: number) => `¥${v}` }]}
        />
      </Box>

      {/* Occupancy chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">场地使用率</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {sportAverages.map(({ sport, label, avg }) => (
              <Typography
                key={sport}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {label} {avg}%
              </Typography>
            ))}
          </Box>
        </Box>
        <LineChart
          loading={occupancyLoading}
          xAxis={[
            { scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 11 } },
          ]}
          yAxis={[{ min: 0, max: 100, valueFormatter: (v: number) => `${v}%` }]}
          series={occupancySeries}
          height={300}
          margin={{ left: 60, right: 20, top: 20, bottom: 60 }}
        />
      </Box>

      {/* Top customers chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">消费排行 Top 10</Typography>
          {restRow && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              其他 {restRow.count > 0 ? `${restRow.count}人` : ''}：¥
              {restRow.amount.toFixed(2)}（{restRow.percentage.toFixed(1)}%）
            </Typography>
          )}
        </Box>
        <BarChart
          loading={topLoading}
          layout="horizontal"
          yAxis={[
            {
              scaleType: 'band',
              data: customerNames,
              tickLabelStyle: { fontSize: 12 },
              width: 130,
            },
          ]}
          xAxis={[{ valueFormatter: (v: number) => `¥${v}` }]}
          series={[
            {
              data: customerAmounts,
              label: '消费金额 (元)',
              color: '#2e7d32',
              valueFormatter: (
                v: number | null,
                { dataIndex }: { dataIndex: number },
              ) => {
                const c = topRows[dataIndex]
                return c ? `¥${(v ?? 0).toFixed(2)} (${c.percentage.toFixed(1)}%)` : ''
              },
              barLabel: (item: BarItem) => {
                const c = topRows[item.dataIndex]
                return c ? `${c.percentage.toFixed(1)}%` : null
              },
            },
          ]}
          height={Math.max(200, customerNames.length * 48 + 60)}
          margin={{ left: 10, right: 20, top: 20, bottom: 40 }}
          hideLegend
          sx={{
            '& .MuiBarLabel-root': { fill: '#fff', fontSize: 12, fontWeight: 'bold' },
          }}
        />
      </Box>
    </Box>
  )
}
